/* eslint-disable max-len */
import icons from '@nespresso-design-system/assets/dist/api/icons/index.json';
import type { FeatureIconProps } from '@nespresso/catalog-components-react/controls/FeatureIcon';
import type { PropertySpecification } from '@/domain/Specification';
import CUP_SIZES from './cupSizes.ts';
import AROMATICS from './aromatics.ts';
import RECIPES from './recipes.ts';

type DSIcons = typeof icons;
type IconSectionName = keyof DSIcons['24'] | keyof DSIcons['32'];

export type IconMappingType = {
  /** The ECAPI supercategories category part, in lowercase */
  supId: string;
  type: IconSectionName;
  /** The DS icon prop name in `[size].[type].*` */
  dsId: string;
  /** The volume of liquid which is coffee */
  mlCoffee?: number;
  /** The volume of liquid which is milk - recipe's contain these values */
  mlMilk?: number;
};

export function getCupSizeDataFromCategoryCode(
  code?: string
): IconMappingType | undefined {
  if (!code) return undefined;
  const cupsizeIcon = CUP_SIZES.find(
    (icon) => icon.supId === code.toLowerCase()
  );
  return cupsizeIcon;
}

export function getRecipesIcon(supId: string): string {
  const icon = RECIPES.find(
    (i) => i.categoryCode.toLowerCase() === supId.toLowerCase()
  );
  return icon?.icon.trim() ?? '';
}

export function getAromaticsIcon(supId: string): string {
  const icon = AROMATICS.find(
    (i) => i.categoryCode.toLowerCase() === supId.toLowerCase()
  );
  return icon?.icon.trim() ?? '';
}

export function millilitresToFluidOunces(mlVal: number): number {
  return mlVal * 0.033814;
}

export function cupSizeVolumeUnitsLabel(unit: 'ml' | 'floz' | '') {
  if (unit === 'ml') return 'ml';
  if (unit === 'floz') return 'fl.oz';
  return '';
}

/**
 * Build feature icons from domain product spec cupsize data
 * Optionally append the liquid volumes, where quantities are in ml or fl.oz
 * Express the final cupsizes as Icon + Label + Quantity, where each are optional
 */
export function productCupSizeSpecToFeatureIcons(
  cupSizeIconSpec: PropertySpecification[],
  displayConfig: {
    showIcon?: boolean;
    /** Will show cupsize label, like Ristretto */
    showCupSizeName?: boolean;
    /** Append liquid volumes to the labels */
    showLiquidVolume?: boolean;
    /** ml or floz */
    preferredUnits: string;
    /** Note: Rounding only applies for calculated volumes, such as fl.oz */
    decimalRounding?: number;
  }
): FeatureIconProps['icons'] {
  const {
    showIcon = true,
    showCupSizeName = true,
    showLiquidVolume = true,
    preferredUnits = 'ml',
    decimalRounding = 2
  } = displayConfig;

  const mapped: FeatureIconProps['icons'] = cupSizeIconSpec.map(
    ({ icon: iconValue = '', title, measure }) => {
      const icon = showIcon ? iconValue : '';
      let label = '';

      if (showCupSizeName) {
        label = title;
      }

      if (showLiquidVolume && typeof measure === 'number') {
        const units: 'floz' | 'ml' =
          preferredUnits.toLowerCase() === 'floz' ? 'floz' : 'ml';
        const volume =
          preferredUnits.toLowerCase() === 'floz'
            ? millilitresToFluidOunces(measure).toFixed(decimalRounding)
            : measure.toString();

        const spacerBeforeUnits = units === 'floz' ? ' ' : '';
        const volumeLabel = `${volume}${spacerBeforeUnits}${cupSizeVolumeUnitsLabel(units)}`;
        // if there's already a label, put the liquid volume in parenthesis
        label += label ? ` (${volumeLabel})` : volumeLabel;
      }
      label = label.trim();

      return {
        icon,
        // label will be like: Espresso or Espresso (40ml) or Espresso (1.35fl)
        label
      };
    }
  );
  return mapped;
}

export function getRecipeIconDetails(
  recipes: PropertySpecification[]
): FeatureIconProps['icons'] {
  return recipes.map((recipe) => ({
    label: recipe.title.toString(),
    icon: recipe.icon ?? ''
  }));
}
